import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { DEFAULT_COLOR_PALETTE } from '@/lib/constants/colors';
import { RateSourceDTO } from '@/modules/common';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { isFeatureEnabled } from '@/modules/common/hooks/use-feature-flags';
import {
  invalidateSuggestedApproversQueries,
  invalidateTeamMemberRatesOnProjectQueries,
  invalidateTimeTrackerProjectsQueries,
  invalidateTimeTrackingQueries,
} from '@/modules/common/utils/query-invalidation';

import { updateAssignment } from '../../api/assignments.api';
import {
  applyOptimisticMemberUpdate,
  cancelAssignmentQueries,
  computeActivePeriods,
  findAssignmentInCache,
  invalidateAllAssignmentQueries,
  OptimisticContext,
  patchCacheAfterMutation,
  refetchActiveAssignmentQueriesInBackground,
  rollbackAssignmentData,
  snapshotAssignmentData,
  unknownPlannerProject,
} from '../../helpers/assignment-cache-utils';
import {
  AssignmentProjectResponse,
  AssignmentResponse,
  PlannerMemberResponse,
  UpdateAssignmentWithUIRequest,
} from '../../types/dto';

/**
 * Hook for updating an existing assignment with optimistic updates.
 * Immediately shows the updated assignment in the UI before the API call completes.
 * Automatically invalidates only the visible date range queries on success.
 * This prevents refetching non-visible data and maintains scroll position.
 *
 * @param options - Optional TanStack Query mutation options
 * @returns Mutation object with mutate, isPending, etc.
 *
 * @example
 * ```typescript
 * const updateMutation = useUpdateAssignment({
 *   onSuccess: () => logger.debug(TAG, 'Assignment updated!')
 * });
 *
 * updateMutation.mutate({
 *   assignmentId: 123,
 *   status: AssignmentStatusDTO.CONFIRMED,
 *   syncToken: 'abc123',
 *   // ... other optional fields to update
 * });
 * ```
 */
export function useUpdateAssignment(
  options?: UseMutationOptions<AssignmentResponse, Error, UpdateAssignmentWithUIRequest>
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.planner');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const { onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation({
    ...restOptions,
    mutationFn: (request: UpdateAssignmentWithUIRequest) => {
      const { payload } = request;
      const isCustomRate = payload.rateSource === RateSourceDTO.CUSTOM;
      // Only send rateSource-dependent fields when rateSource is present in the payload;
      // otherwise the PATCH omits them entirely (undefined = not sent).
      const updateAssignmentRequest =
        payload.rateSource !== undefined
          ? {
              ...payload,
              customRate: isCustomRate ? payload.customRate : null,
              customRateCurrencyCode: isCustomRate ? payload.customRateCurrencyCode : null,
            }
          : payload;
      return updateAssignment(updateAssignmentRequest);
    },
    onMutate: async (variables: UpdateAssignmentWithUIRequest): Promise<OptimisticContext> => {
      const { payload } = variables;

      // Cancel outgoing refetches to prevent overwriting optimistic update
      await cancelAssignmentQueries(queryClient);

      // Snapshot previous values for rollback
      const previousData = snapshotAssignmentData(queryClient);

      // Find the assignment's current dates and owner for utilization recalculation
      const cachedAssignment = findAssignmentInCache(queryClient, payload.assignmentId);
      const oldAssignmentDates = cachedAssignment?.dates;
      const assignmentUserId = cachedAssignment?.userId;

      // Helper to update assignment in a member
      const updateAssignmentInMember = (member: PlannerMemberResponse): PlannerMemberResponse => {
        const updatedAssignments = member.assignments.map(plannerAssignment => {
          if (plannerAssignment.assignment.id !== payload.assignmentId) {
            return plannerAssignment;
          }

          // Merge update fields with existing assignment
          const updatedAssignment: AssignmentResponse = {
            ...plannerAssignment.assignment,
            ...(payload.startDate !== undefined && { startDate: payload.startDate }),
            ...(payload.endDate !== undefined && { endDate: payload.endDate }),
            ...(payload.status !== undefined && { status: payload.status }),
            ...(payload.doSkipWeekdays !== undefined && {
              doSkipWeekdays: payload.doSkipWeekdays,
            }),
            ...(payload.isBillable !== undefined && { isBillable: payload.isBillable }),
            ...(payload.label !== undefined && { label: payload.label }),
            ...(payload.minutesPerDay !== undefined && {
              minutesPerDay: payload.minutesPerDay,
            }),
            ...(payload.note !== undefined && { note: payload.note }),
            ...(payload.isBackgroundWork !== undefined && {
              isBackgroundWork: payload.isBackgroundWork,
            }),
            ...(payload.probability !== undefined && { probability: payload.probability }),
            ...(payload.rateSource !== undefined && { rateSource: payload.rateSource }),
            ...(payload.customRate !== undefined && {
              customRate: payload.customRate ?? undefined,
            }),
            // Update project info if project changed
            ...(payload.projectId !== undefined && {
              project: {
                id: payload.projectId,
                name: variables.ui?.projectName || plannerAssignment.assignment.project?.name || '',
                colorHex: variables.ui?.projectColorHex
                  ? variables.ui.projectColorHex.replace('#', '')
                  : plannerAssignment.assignment.project?.colorHex ||
                    DEFAULT_COLOR_PALETTE.slate.replace('#', ''),
              } as AssignmentProjectResponse,
            }),
          };

          // Recompute active periods if dates or doSkipWeekdays changed
          const datesOrSkipChanged =
            payload.startDate !== undefined ||
            payload.endDate !== undefined ||
            payload.doSkipWeekdays !== undefined;

          const activePeriods = datesOrSkipChanged
            ? computeActivePeriods(
                updatedAssignment.startDate,
                updatedAssignment.endDate,
                updatedAssignment.doSkipWeekdays,
                plannerAssignment.activePeriods[0]?.rate ?? null
              )
            : plannerAssignment.activePeriods;

          // The old total no longer applies; unknown until the on-success refresh
          const bookedMinutesFieldsChanged =
            datesOrSkipChanged ||
            payload.minutesPerDay !== undefined ||
            payload.isBackgroundWork !== undefined;
          const bookedMinutes = bookedMinutesFieldsChanged ? null : plannerAssignment.bookedMinutes;

          return {
            ...plannerAssignment,
            assignment: updatedAssignment,
            bookedMinutes,
            // The new project's client/probability/status is unknown at optimistic
            // time; corrected by the on-success invalidation
            ...(payload.projectId !== undefined && {
              project: unknownPlannerProject(payload.projectId),
            }),
            activePeriods,
          };
        });

        return {
          ...member,
          assignments: updatedAssignments,
        };
      };

      // Optimistically update all three cache views (byMember, byDepartment, byProject)
      applyOptimisticMemberUpdate(queryClient, updateAssignmentInMember);

      return { ...previousData, oldAssignmentDates, assignmentUserId };
    },
    onSuccess: (data, variables, context) => {
      if (isFeatureEnabled('usePlannerCachePatching')) {
        const ctx = context as OptimisticContext;

        const newDates = {
          startDate:
            variables.payload.startDate ?? ctx.oldAssignmentDates?.startDate ?? data.startDate,
          endDate: variables.payload.endDate ?? ctx.oldAssignmentDates?.endDate ?? data.endDate,
        };

        // Single atomic cache update: patch syncToken + recalculate utilization
        patchCacheAfterMutation(queryClient, {
          assignmentId: variables.payload.assignmentId,
          serverData: data,
          userId: ctx.assignmentUserId,
          oldDates: ctx.oldAssignmentDates,
          newDates,
          customRateCurrencyCode: variables.payload.customRateCurrencyCode ?? undefined,
        });

        // Fire-and-forget background refetch to reconcile activePeriods from backend
        // (client-side cannot compute rate-change-based period splits).
        refetchActiveAssignmentQueriesInBackground(queryClient);
      } else {
        invalidateAllAssignmentQueries(queryClient);
      }

      // Assignment changes can affect rate calculations for future queries
      invalidateTeamMemberRatesOnProjectQueries(queryClient);
      // Assignment changes affect booked hours shown on the time tracking page
      invalidateTimeTrackingQueries(queryClient);
      // Assignment changes can alter the time-off suggested approvers.
      invalidateSuggestedApproversQueries(queryClient);
      // Assignment changes can flip which projects count as "booked" in the
      // Time Tracking modal's project picker.
      invalidateTimeTrackerProjectsQueries(queryClient);

      displayToastSuccess(
        t('toasts.assignmentUpdatedSuccess', {
          userName: variables.ui?.userName ?? '',
          projectName: variables.ui?.projectName ?? '',
        })
      );

      onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      // Rollback optimistic updates on error
      rollbackAssignmentData(queryClient, context as OptimisticContext);

      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.planner:toasts.updateAssignmentError',
        {
          interpolation: {
            userName: variables.ui?.userName ?? '',
            projectName: variables.ui?.projectName ?? '',
          },
        }
      );

      onError?.(error, variables, context);
    },
  });
}
